import { onBeforeUnmount, ref, watch } from "vue";
import { pickActiveContentsId } from "@/utils/explanationContents.mjs";

export function useExplanationContentsScroll(options) {
  const { bodyRef, itemIds, topOffset, contentKey, scrollRootRef } = options;

  const activeId = ref(null);

  const visibleIds = new Set();
  let scrollSpyObserver = null;

  const optionValue = (source) =>
    typeof source === "function" ? source() : source?.value ?? source;

  const orderedIds = () => {
    const value = optionValue(itemIds);
    return Array.isArray(value) ? value : [];
  };

  const offset = () => Number(optionValue(topOffset) ?? 0);
  const contentDependency = () => optionValue(contentKey);
  const scrollRoot = () => optionValue(scrollRootRef) ?? null;

  const disconnectScrollSpy = () => {
    if (scrollSpyObserver) {
      scrollSpyObserver.disconnect();
      scrollSpyObserver = null;
    }
    visibleIds.clear();
  };

  const setupScrollSpy = () => {
    disconnectScrollSpy();

    const root = bodyRef?.value;
    const ids = orderedIds();
    if (!root || typeof IntersectionObserver === "undefined" || !ids.length) {
      activeId.value = ids[0] ?? null;
      return;
    }

    activeId.value = pickActiveContentsId(ids, visibleIds, activeId.value);
    scrollSpyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visibleIds.add(entry.target.id);
          else visibleIds.delete(entry.target.id);
        }
        activeId.value = pickActiveContentsId(
          orderedIds(),
          visibleIds,
          activeId.value,
        );
      },
      {
        root: scrollRoot(),
        rootMargin: `-${offset()}px 0px -55% 0px`,
        threshold: 0,
      },
    );

    for (const id of ids) {
      const element = root.querySelector(`#${id}`);
      if (element) scrollSpyObserver.observe(element);
    }
  };

  watch(
    () => [
      bodyRef?.value,
      scrollRoot(),
      orderedIds().join("|"),
      offset(),
      contentDependency(),
    ],
    setupScrollSpy,
    { immediate: true, flush: "post" },
  );

  onBeforeUnmount(disconnectScrollSpy);

  return { activeId };
}
